import type { ContactType } from '../../types';

export type LeadCategoryOption = { key: string; label: string; color?: string };

/** Bestehender Kunde zum Vorschlag (Server: inboxCustomerMatchCore). */
export type InboxCustomerMatch =
  | {
      kind: 'organization';
      organization_id: string;
      organization_name: string;
      contact_id: string | null;
      reason: 'company' | 'email';
    }
  | {
      kind: 'contact';
      contact: {
        id: string;
        first_name: string | null;
        last_name: string | null;
        name: string | null;
        company: string | null;
        email: string | null;
        phone: string | null;
        address: string | null;
        organization_id: string | null;
      };
      reason: 'email' | 'name';
    }
  | { kind: 'company_hint'; company: string; contact_labels: string[] };

export type InboxSuggestLeadResponse = {
  source?: string;
  summary?: string;
  detail_note?: string;
  lead_title?: string;
  category?: string;
  notes?: string;
  warning?: string | null;
  customer_match?: InboxCustomerMatch | null;
  contact?: {
    contact_type?: string;
    first_name?: string;
    last_name?: string;
    company?: string;
    email?: string;
    phone?: string;
    address?: string;
  };
};

export type InboxSuggestLeadApplied = {
  formPatch: {
    lead_title: string;
    category: string;
    notes: string;
    contact_type: ContactType;
    first_name: string;
    last_name: string;
    company: string;
    email: string;
    phone: string;
    address: string;
  };
  prefillDetailNote: string;
  prefillWarning: string | null;
  customerMatch: InboxCustomerMatch | null;
};

const resultCache = new Map<string, InboxSuggestLeadApplied>();
const inflightByAnchor = new Map<string, Promise<InboxSuggestLeadApplied>>();

export function getCachedInboxSuggestLead(anchorId: string): InboxSuggestLeadApplied | undefined {
  return resultCache.get(anchorId);
}

export function invalidateInboxSuggestLeadCache(anchorId: string): void {
  resultCache.delete(anchorId);
  inflightByAnchor.delete(anchorId);
}

export function applyInboxSuggestLeadResponse(
  data: InboxSuggestLeadResponse,
  loadedCategories: LeadCategoryOption[],
): InboxSuggestLeadApplied {
  const c = data.contact ?? {};
  const contactType: ContactType =
    c.contact_type === 'Gewerblicher Kunde' ? 'Gewerblicher Kunde' : 'Privatkunde';
  const suggested = (data.category ?? '').trim().toLowerCase();
  const matched = suggested
    ? loadedCategories.find(
        (cat) => cat.key.trim().toLowerCase() === suggested || cat.label.trim().toLowerCase() === suggested,
      )
    : undefined;

  return {
    formPatch: {
      lead_title: (data.lead_title ?? '').trim(),
      category: matched?.key ?? '',
      notes: (data.notes ?? '').trim(),
      contact_type: contactType,
      first_name: (c.first_name ?? '').trim(),
      last_name: (c.last_name ?? '').trim(),
      company: (c.company ?? '').trim(),
      email: (c.email ?? '').trim(),
      phone: (c.phone ?? '').trim(),
      address: (c.address ?? '').trim(),
    },
    prefillDetailNote: (data.detail_note ?? '').trim(),
    prefillWarning: data.warning ? String(data.warning) : null,
    customerMatch: data.customer_match ?? null,
  };
}

export async function fetchInboxSuggestLeadApplied(
  anchorId: string,
  loadCategories: () => Promise<LeadCategoryOption[]>,
): Promise<InboxSuggestLeadApplied> {
  const cached = resultCache.get(anchorId);
  if (cached) return cached;

  const existing = inflightByAnchor.get(anchorId);
  if (existing) return existing;

  const promise = (async () => {
    const loadedCategories = await loadCategories();
    const res = await fetch('/api/inbox/suggest-lead', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ anchor_email_id: anchorId }),
    });
    if (!res.ok) {
      let detail = res.statusText;
      try {
        const ct = res.headers.get('content-type');
        if (ct?.includes('application/json')) {
          const j = (await res.json()) as { error?: string };
          if (j?.error) detail = j.error;
        } else {
          const t = await res.text();
          if (t) detail = t.slice(0, 200);
        }
      } catch {
        /* ignore */
      }
      throw new Error(detail);
    }
    const data = (await res.json()) as InboxSuggestLeadResponse;
    const applied = applyInboxSuggestLeadResponse(data, loadedCategories);
    resultCache.set(anchorId, applied);
    return applied;
  })();

  inflightByAnchor.set(anchorId, promise);
  try {
    return await promise;
  } finally {
    if (inflightByAnchor.get(anchorId) === promise) {
      inflightByAnchor.delete(anchorId);
    }
  }
}
